---
title: "Najczęstsze usterki przy odbiorze mieszkania — na co zwrócić uwagę"
description: "Najczęstsze usterki przy odbiorze mieszkania — lista konkretnych wad do sprawdzenia podczas odbioru technicznego od dewelopera, zanim podpiszesz protokół."
date: "2026-06-07"
slug: "najczestsze-usterki-odbior-mieszkania"
faq:
  - q: "Czy mogę odmówić podpisania protokołu odbioru?"
    a: "Tak — jeśli stwierdzone wady są istotne (uniemożliwiają normalne korzystanie z mieszkania), masz prawo odmówić odbioru. Odmowę wpisuje się do protokołu wraz z uzasadnieniem. Deweloper ma obowiązek usunąć wady i wyznaczyć nowy termin odbioru."
  - q: "Ile czasu deweloper ma na usunięcie usterek?"
    a: "Zgodnie z ustawą deweloperską — do 30 dni od sporządzenia protokołu. W przypadkach uzasadnionych deweloper może wyznaczyć dłuższy termin (do 60 dni), informując nabywcę na piśmie."
  - q: "Czy warto wynająć inspektora technicznego na odbiór?"
    a: "Tak. Profesjonalny inspektor wykrywa niewidoczne gołym okiem usterki (kamerą termowizyjną, miernikiem wilgoci, czujnikiem napięcia). Koszt usługi (300–600 zł) jest znacznie niższy niż koszt napraw po zasiedleniu."
  - q: "Co to jest wada istotna a nieistotna przy odbiorze?"
    a: "Wada istotna uniemożliwia normalne korzystanie z mieszkania lub stwarza zagrożenie (np. nieczynna instalacja elektryczna, poważne spękania konstrukcji). Wada nieistotna to drobne niedoskonałości wykończenia. Klasyfikacja wpływa na prawo do odmowy odbioru."
  - q: "Jak długo trwa rękojmia dewelopera?"
    a: "Rękojmia za wady fizyczne nieruchomości trwa 5 lat od przeniesienia własności na nabywcę. Gwarancja (jeśli deweloper jej udziela) może być krótsza lub dłuższa — sprawdź zapisy umowy."
---

Najczęstsze usterki przy odbiorze mieszkania to temat, który interesuje każdego, kto staje przed odbiorem technicznym od dewelopera. Wiedząc, gdzie szukać wad, masz szansę wpisać je do protokołu i wyegzekwować naprawę w ramach rękojmi — zanim klucze trafią do twojej kieszeni.

> Szukasz informacji o tym, co musi zawierać sam protokół odbioru? Przeczytaj nasz artykuł: [Protokół odbioru mieszkania — co musi zawierać](/blog/protokol-odbioru-mieszkania-co-zawiera).

---

## Dlaczego warto szukać usterek przed podpisaniem protokołu?

Podpisanie protokołu odbioru bez wpisania stwierdzonych usterek jest ryzykowne. W świetle ustawy deweloperskiej deweloper ma 30 dni (a w uzasadnionych przypadkach 60 dni) na usunięcie usterek wpisanych do protokołu. Usterki zgłoszone po odbiorze wciąż mogą być objęte rękojmią (5 lat od przeniesienia własności), ale udowodnienie ich istnienia jest trudniejsze bez dokumentacji z dnia odbioru.

Warto zabrać na odbiór inspektora technicznego — profesjonalista wyposaży się w poziomicę, miernik wilgoci, kamerę termowizyjną i czujnik napięcia.

---

## Najczęstsze usterki przy odbiorze mieszkania — pełna lista

### Posadzki i podłogi

- **Nierówności posadzki** — sprawdź łatą 2-metrową: dopuszczalna odchyłka wynosi zazwyczaj 3 mm na 2 m (sprawdź normy projektowe dla danej inwestycji).
- **Pęknięcia wylewki** — widoczne rysy w jastrychu betonowym mogą oznaczać błędy technologiczne.
- **Odklejone lub pęknięte płytki** — opukaj każdą płytkę: pusty odgłos oznacza brak przyczepności.
- **Brak dylatacji** — przy dużych powierzchniach płytek (powyżej 25–30 m²) powinny być szczeliny dylatacyjne.
- **Zarysowania na posadzce z drewna lub paneli** — nawet drobne, wpisz do protokołu.

### Ściany i sufity

- **Nierówności tynku** — przyjmij poziomicę lub łatę: norma zwykle nie dopuszcza odchyłek powyżej 3 mm na 1 m.
- **Pęknięcia i rysy** — poziome i ukośne na ścianach nośnych wymagają oceny konstruktora.
- **Zacieki i plamy wilgoci** — szczególnie na sufitach i narożach zewnętrznych ścian.
- **Nierówne malowanie** — prześwitujące warstwy, smugi, różnice odcienia.
- **Brak narożników i listew** — uszkodzone lub brakujące wykończenia krawędzi.

### Okna i drzwi

- **Nieszczelne okna** — przełóż kartkę papieru między skrzydłem a ramą i przymknij: jeśli daje się wyciągnąć bez oporu, uszczelnienie jest niewystarczające.
- **Rysowania na szybach i ramach** — sprawdź pod kątem pod różnym oświetleniem.
- **Nieprawidłowe zamknięcia i okucia** — każdy zamek, klamka, zawiasy powinny działać bez oporu.
- **Drzwi wejściowe** — sprawdź poziomowanie, szczelność progów, działanie zamków wielopunktowych.
- **Parapety zewnętrzne** — odpowiedni spadek do odprowadzania wody, brak pęknięć.

### Łazienka i WC

- **Szczelność fugi przy wannie i kabinie prysznicowej** — szczelina bez fugi to pewna przyszła nieszczelność.
- **Odpływ posadzki** — sprawdź lewając kubek wody, czy spływa w kierunku odpływu.
- **Baterie i zawory** — otwórz każdą baterię: ciśnienie, temperatura, brak przecieków.
- **Cisterna i spłuczka** — ustaw oba tryby spłukiwania, sprawdź szczelność.
- **Wentylacja** — przyłóż kartkę A4 do kratki: powinna przyciągać.

### Instalacja elektryczna

- **Brak napięcia w gniazdach** — sprawdź każde gniazdo prostym testerem lub ładowarką.
- **Brakujące lub nieprawidłowo zamocowane gniazda i włączniki** — ruszające się, krzywe.
- **Rozdzielnia** — czy opisane bezpieczniki odpowiadają obwodom? Czy wyłączniki RCD działają?
- **Brak ochrony przeciwporażeniowej** — tester gniazdek wskaże brak uziemienia.
- **Niedokończone prace** — brakujące ramki, nieosłonięte puszki.

### Instalacja wod-kan i ogrzewanie

- **Szczelność przyłączy** — sprawdź wzrokowo pod umywalkami, zlewem, za pralką (jeśli podłączona).
- **Grzejniki** — czy zamontowane, prostno, z termostatami? Przekręć zawory regulacyjne.
- **Ogrzewanie podłogowe** — konieczna rozruchowa regulacja; sprawdź, czy rozdzielacz jest dostępny.
- **Ciśnienie wody** — powinno być wystarczające przy jednoczesnym otwarciu kilku punktów.

### Balkon, taras i loggia

- **Posadzka** — odpowiedni spadek (min. 1–1,5%), brak spękań, szczelna fuga przy elewacji.
- **Balustrada** — sztywność, wysokość (min. 110 cm dla wysokości powyżej 12 m), stan spawów.
- **Odpływ** — czy nie jest zatkany lub źle umieszczony?

### Wentylacja

- **Kratki wentylacyjne** — każda powinna mieć ciąg (test kartką). Kratki w kuchni, łazience i WC.
- **Rekuperacja (jeśli dotyczy)** — sprawdź działanie nawiewów i wywiewów.

---

## Jak dokumentować usterki na odbioru?

Każda usterka powinna zostać:

1. **Opisana** — lokalizacja (pomieszczenie, ściana, element) i charakter wady.
2. **Sfotografowana** — zdjęcie z datą i GPS jako dowód stanu w dniu odbioru.
3. **Wpisana do protokołu** — z oceną istotności (wada istotna / nieistotna).

Nie wstydź się wypełniać długiej listy. Deweloper musi się do niej odnieść w terminie ustawowym. Więcej o samym protokole: [Protokół odbioru mieszkania — co musi zawierać](/blog/protokol-odbioru-mieszkania-co-zawiera).

---

## Jak Pinveo wspiera dokumentowanie usterek?

Pinveo jest zaprojektowane tak, by inspektor lub świadomy nabywca mógł sprawnie udokumentować wszystkie usterki:

- **Markery na rzucie PDF** — oznaczasz usterkę bezpośrednio na planie mieszkania — każda wada ma precyzyjną lokalizację.
- **Zdjęcia z GPS i datą** — każde zdjęcie automatycznie zawiera geolokalizację i znacznik czasu.
- **Formularze odbioru** — gotowe listy kontrolne dla każdego pomieszczenia.
- **Raport PDF z listą usterek** — gotowy dokument do podpisania przez obie strony.
- **Praca offline** — działa bez zasięgu wewnątrz budynku.

Więcej o odbiorach mieszkań z Pinveo: [Odbiory mieszkań z Pinveo](/zastosowania/odbiory-mieszkan).

---

## Podsumowanie

Najczęstsze usterki przy odbiorze mieszkania dotyczą posadzek, ścian, stolarki okiennej i drzwiowej, instalacji elektrycznej, wod-kan oraz wentylacji. Systematyczne sprawdzanie każdego pomieszczenia według listy kontrolnej i staranna dokumentacja fotograficzna to podstawa skutecznego odbioru. Pinveo skraca czas dokumentowania i zapewnia gotowy raport PDF z lokalizacją każdej usterki na rzucie.

---

## FAQ

### Czy mogę odmówić podpisania protokołu odbioru?

Tak — jeśli stwierdzone wady są **istotne** (uniemożliwiają normalne korzystanie z mieszkania), masz prawo odmówić odbioru. Odmowę wpisuje się do protokołu wraz z uzasadnieniem. Deweloper ma obowiązek usunąć wady i wyznaczyć nowy termin odbioru.

### Ile czasu deweloper ma na usunięcie usterek?

Zgodnie z ustawą deweloperską — do 30 dni od sporządzenia protokołu. W przypadkach uzasadnionych deweloper może wyznaczyć dłuższy termin (do 60 dni), informując nabywcę na piśmie.

### Czy warto wynająć inspektora technicznego na odbiór?

Tak. Profesjonalny inspektor wykrywa niewidoczne gołym okiem usterki (kamerą termowizyjną, miernikiem wilgoci, czujnikiem napięcia). Koszt usługi (300–600 zł) jest znacznie niższy niż koszt napraw po zasiedleniu.

### Co to jest wada istotna a nieistotna przy odbiorze?

Wada istotna uniemożliwia normalne korzystanie z mieszkania lub stwarza zagrożenie (np. nieczynna instalacja elektryczna, poważne spękania konstrukcji). Wada nieistotna to drobne niedoskonałości wykończenia. Klasyfikacja wpływa na prawo do odmowy odbioru.

### Jak długo trwa rękojmia dewelopera?

Rękojmia za wady fizyczne nieruchomości trwa 5 lat od przeniesienia własności na nabywcę. Gwarancja (jeśli deweloper jej udziela) może być krótsza lub dłuższa — sprawdź zapisy umowy.
